import { Box, Typography, Divider, Stack } from "@mui/material";
import {
  GavelOutlined,
  HowToRegOutlined,
  BuildOutlined,
  PrivacyTipOutlined,
  CopyrightOutlined,
  BlockOutlined,
  ReportProblemOutlined,
  UpdateOutlined,
  PublicOutlined,
  EmailOutlined,
} from "@mui/icons-material";
import { Helmet } from "react-helmet-async";
import BackButton from "../components/utils/BackButton";

export default function TermsConditions() {
  return (
    <>
      <Helmet>
        <title>Terms and Conditions | MarriageBiodata.live</title>
        <meta
          name="description"
          content="Read the Terms and Conditions for using MarriageBiodata.live, a free online marriage biodata creation tool."
        />
        <link
          rel="canonical"
          href="https://www.marriagebiodata.live/terms-conditions"
        />
      </Helmet>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://www.marriagebiodata.live",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Terms and Conditions",
              item: "https://www.marriagebiodata.live/terms-conditions",
            },
          ],
        })}
      </script>

      <Box
        component="main"
        sx={{
          maxWidth: 1000,
          mx: "auto",
          px: { xs: 2, md: 4 },
          py: { xs: 4, md: 6 },
          lineHeight: 1.75,
        }}
      >
        <BackButton fallback="/" />
        {/* Header */}
        <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
          <GavelOutlined sx={{ color: "warning.main" }} />
          <Typography variant="h4" fontWeight={700}>
            Terms and Conditions
          </Typography>
        </Stack>

        <Typography paragraph color="text.secondary">
          These Terms and Conditions govern your use of MarriageBiodata.live, a
          free online marriage biodata creation web application. By accessing or
          using this website, you agree to comply with these terms.
        </Typography>

        <Divider sx={{ my: 4 }} />

        {/* Section 1 */}
        <Section
          icon={<HowToRegOutlined />}
          title="1. Eligibility"
        >
          This service is intended for users who are legally permitted to use
          online services in their jurisdiction. By using this website, you
          confirm that you meet all applicable legal requirements.
        </Section>

        {/* Section 2 */}
        <Section
          icon={<BuildOutlined />}
          title="2. Service Usage"
        >
          MarriageBiodata.live provides a free online tool to create marriage
          biodata for personal and non-commercial use only. Users are solely
          responsible for the accuracy, completeness, and authenticity of the
          information entered.
        </Section>

        {/* Section 3 */}
        <Section
          icon={<PrivacyTipOutlined />}
          title="3. Data and Privacy"
        >
          All biodata information is processed locally within your browser. We do
          not collect, store, or transmit personal data. Please review our
          Privacy Policy for a detailed explanation of our privacy practices.
        </Section>

        {/* Section 4 */}
        <Section
          icon={<CopyrightOutlined />}
          title="4. Intellectual Property"
        >
          All website content, design, branding, and software are the property of
          MarriageBiodata.live unless otherwise stated. You may not copy,
          reproduce, modify, or redistribute any part of this website without
          prior permission.
        </Section>

        {/* Section 5 */}
        <Section
          icon={<BlockOutlined />}
          title="5. Prohibited Activities"
        >
          Users must not misuse the website, attempt unauthorized access,
          introduce malicious code, upload harmful content, or interfere with
          the website’s functionality or security.
        </Section>

        {/* Section 6 */}
        <Section
          icon={<ReportProblemOutlined />}
          title="6. Limitation of Liability"
        >
          This service is provided on an "as is" basis without warranties of any
          kind. MarriageBiodata.live shall not be liable for any direct,
          indirect, incidental, or consequential damages arising from the use
          or inability to use the website.
        </Section>

        {/* Section 7 */}
        <Section
          icon={<UpdateOutlined />}
          title="7. Changes to Terms"
        >
          We reserve the right to update or modify these Terms and Conditions at
          any time. Continued use of the website after changes are made
          constitutes acceptance of the revised terms.
        </Section>

        {/* Section 8 */}
        <Section
          icon={<PublicOutlined />}
          title="8. Governing Law"
        >
          These Terms and Conditions shall be governed by and interpreted in
          accordance with the laws of India.
        </Section>

        {/* Section 9 */}
        <Section
          icon={<EmailOutlined />}
          title="9. Contact Information"
        >
          If you have any questions regarding these Terms and Conditions, you may
          contact us using the email address provided on the website.
        </Section>
      </Box>
    </>
  );
}

/* ---------- Reusable Section Wrapper ---------- */
function Section({ icon, title, children }) {
  return (
    <Box mt={4}>
      <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
        <Box sx={{ color: "warning.main" }}>{icon}</Box>
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
      </Stack>
      <Typography color="text.secondary">{children}</Typography>
    </Box>
  );
}
